import { z } from 'zod'

export const poLineItemSchema = z.object({
  variant_id: z.number().int().positive(),
  product_id: z.number().int().positive(),
  product_title: z.string().min(1),
  variant_title: z.string(),
  sku: z.string(),
  barcode: z.string(),
  // Session 7.5 — per-item vendor. A supplier-centric PO carries items from
  // multiple vendor brands; this field is what each picked line item is
  // tagged with so receiving and reporting still attribute units to the
  // right brand.
  vendor: z.string().min(1, 'Vendor required'),
  retail_price: z.number().nonnegative(),
  cost_base: z.number().positive('Cost must be > 0'),
  qty_ordered: z.number().int().min(1, 'Qty must be ≥ 1'),
  notes: z.string(),
  // Display-only context pulled from the dashboard at pick time. Not sent to backend.
  on_hand: z.number().int().optional(),
  incoming: z.number().int().optional(),
  rec_qty: z.number().int().optional(),
  // Display-only units received so far (from the loaded PO's line item). Carried
  // through so the View-PO modal can show a Received column on ordered/archived
  // POs. Undefined during create/draft (no receiving yet) → column stays hidden.
  // Never sent to the backend (the create/update request mappers pick fields
  // explicitly and omit this).
  qty_received: z.number().int().optional(),
  // Display-only ISO timestamp of the most recent receipt (from po_scan_log),
  // shown under the Received qty in the modal. Never sent back to the backend.
  last_received_at: z.string().nullish(),
  signal: z.string().optional(),
  return_rate: z.number().optional(),
})

export const createPOSchema = z
  .object({
    // Session 7.5: a PO has one *recipient* — either a supplier (multi-brand
    // umbrella) or a single vendor (single-brand). Only ~4 of 100+ vendors
    // have a supplier configured, so vendor-only POs remain the common case;
    // the supplier path is for the smaller multi-brand subset.
    vendor: z.string().optional(),
    supplier: z.string().optional(),
    location_to_receive: z.string().min(1, 'Location required'),
    notes: z.string(),
    items: z.array(poLineItemSchema).min(1, 'At least one line item'),
    // expected_delivery rides along on Create/Update; vendor_po_ref is still
    // local-only (the backend doesn't accept it yet, so the request mappers
    // drop it before POST).
    expected_delivery: z.string().optional(),
    vendor_po_ref: z.string().optional(),
    // Fresh "add a note" input on the Review step. Never part of the
    // Create/PATCH payload — it's sent as the `{ notes }` body on submit /
    // resubmit / approve, which APPENDS a line to the server-managed
    // approval_notes thread (blank sends nothing, so an untouched box never
    // creates a thread entry).
    approval_notes: z.string().optional(),
  })
  .refine(
    (data) =>
      (data.supplier ?? '').trim().length > 0 ||
      (data.vendor ?? '').trim().length > 0,
    {
      message: 'Pick a supplier or vendor',
      // Attach to `vendor` so RHF surfaces the message under that field; the
      // wizard's recipient picker renders the same FormMessage either way.
      path: ['vendor'],
    }
  )

export type POFormValues = z.infer<typeof createPOSchema>
export type POLineItemFormValues = z.infer<typeof poLineItemSchema>

// Friendly labels for line-item fields that can fail validation on a field the
// Items table doesn't render (variant_id / product_id / vendor / cost_base).
// Without these, a failed `trigger('items')` makes the "Review" button look
// dead — RHF has no visible FormMessage to attach to.
const ITEM_FIELD_LABELS: Record<string, string> = {
  variant_id: 'product not added from search (no variant)',
  product_id: 'product not added from search (no product)',
  product_title: 'missing product name',
  vendor: 'missing vendor',
  cost_base: 'cost must be greater than 0',
  qty_ordered: 'quantity must be at least 1',
  retail_price: 'invalid retail price',
}

type ItemErrorEntry = Record<string, { message?: string } | undefined> | undefined

// Turn RHF's `errors.items` (+ the current line values, for naming the line)
// into a one-line, human-readable reason the Review step is blocked. Returns
// null when there's nothing actionable to report.
export function describeItemsError(
  itemsError: unknown,
  items: Array<Partial<POLineItemFormValues>>
): string | null {
  // Array-level error (e.g. the .min(1) "At least one line item").
  if (itemsError && typeof itemsError === 'object' && 'message' in itemsError) {
    const msg = (itemsError as { message?: string }).message
    if (msg) return msg
  }
  if (!Array.isArray(itemsError)) return null

  const problems: string[] = []
  itemsError.forEach((entry: ItemErrorEntry, idx: number) => {
    if (!entry) return
    const line = items[idx] ?? {}
    const label = (line.product_title || line.sku || `Line ${idx + 1}`) as string
    const reasons = Object.keys(entry)
      .filter((k) => entry[k])
      .map((k) => ITEM_FIELD_LABELS[k] ?? entry[k]?.message ?? k)
    if (reasons.length) problems.push(`${label}: ${reasons.join(', ')}`)
  })
  if (!problems.length) return null
  return `Can't continue — ${problems.join('; ')}.`
}
